/**
 * ComplianceReportCard Component
 * ===============================
 *
 * Displays the result of an async compliance check.
 * Shows risk level, risk score, entities checked, and key findings.
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Alert,
  AlertTitle,
  LinearProgress,
  Button,
  Collapse,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  Public as GlobalIcon,
  Inventory as ProductIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { ComplianceReport } from '../../../services/complianceService';

interface ComplianceReportCardProps {
  /** The compliance report to display */
  report: ComplianceReport;
  /** Callback to view full report details */
  onViewDetails?: () => void;
  /** Whether to show expanded details by default */
  defaultExpanded?: boolean;
}

const getRiskColor = (riskLevel: string) => {
  switch (riskLevel?.toLowerCase()) {
    case 'critical':
      return 'error';
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    case 'minimal':
      return 'success';
    default:
      return 'info'; // Changed from 'default' to 'info' - valid MUI color
  }
};

const getStatusIcon = (status: string, theme: any) => {
  switch (status?.toLowerCase()) {
    case 'compliant':
      return <CheckIcon sx={{ color: theme.palette.success.main }} />;
    case 'non_compliant':
      return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
    case 'requires_review':
    case 'conditional':
      return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
    default:
      return <InfoIcon sx={{ color: theme.palette.info.main }} />;
  }
};

const getStatusLabel = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'compliant':
      return 'Compliant';
    case 'non_compliant':
      return 'Non-Compliant';
    case 'requires_review':
      return 'Requires Review';
    case 'conditional':
      return 'Conditional';
    case 'pending':
      return 'Pending';
    default:
      return status;
  }
};

export const ComplianceReportCard: React.FC<ComplianceReportCardProps> = ({
  report,
  onViewDetails,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const theme = useTheme();

  if (!report) {
    return null;
  }

  const riskScore = report.risk_assessment?.risk_score || report.risk_assessment?.overall_risk_score || 0;
  const riskLevel = report.risk_assessment?.risk_level || 'unknown';
  const riskColor = getRiskColor(riskLevel);

  // Extract entities checked from the report
  const entitiesChecked = report.sanctions_check?.length || 0;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon(report.overall_status, theme)}
            <Typography variant="h6">
              Compliance Check Complete
            </Typography>
          </Box>
          <Chip
            label={getStatusLabel(report.overall_status)}
            color={report.overall_status === 'compliant' ? 'success' : 'warning'}
            size="small"
          />
        </Box>

        {/* Risk Assessment */}
        <Alert severity={riskColor as 'success' | 'warning' | 'error' | 'info'} sx={{ mb: 2 }}>
          <AlertTitle>Risk Level: {riskLevel?.toUpperCase()}</AlertTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Risk Score
              </Typography>
              <LinearProgress
                variant="determinate"
                value={riskScore}
                sx={{ height: 8, borderRadius: 1, mt: 0.5 }}
                color={riskColor as 'success' | 'warning' | 'error'}
              />
            </Box>
            <Typography variant="h6" sx={{ minWidth: 50 }}>
              {Math.round(riskScore)}/100
            </Typography>
          </Box>
        </Alert>

        {/* Summary Grid */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
              <BusinessIcon sx={{ color: theme.palette.primary.main, mb: 0.5 }} />
              <Typography variant="h6">{entitiesChecked}</Typography>
              <Typography variant="caption" color="text.secondary">
                Entities Checked
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
              <GlobalIcon sx={{ color: theme.palette.primary.main, mb: 0.5 }} />
              <Typography variant="h6">{report.jurisdictions_checked?.length || 0}</Typography>
              <Typography variant="caption" color="text.secondary">
                Lists Checked
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
              <ProductIcon sx={{ color: theme.palette.primary.main, mb: 0.5 }} />
              <Typography variant="h6">
                {(report.processing_time_ms || report.check_duration_ms || 0) / 1000}s
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Processing Time
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Expandable Details */}
        <Divider sx={{ my: 2 }} />
        <Button
          fullWidth
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ justifyContent: 'space-between' }}
        >
          <Typography variant="body2">
            {expanded ? 'Hide Details' : 'Show Details'}
          </Typography>
        </Button>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {/* Sanctions Check Results */}
            {report.sanctions_check && report.sanctions_check.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Entities Screened:
                </Typography>
                <List dense>
                  {report.sanctions_check.map((entity: any, index: number) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {entity.is_sanctioned ? (
                          <ErrorIcon fontSize="small" color="error" />
                        ) : (
                          <CheckIcon fontSize="small" color="success" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={entity.entity_name}
                        secondary={
                          entity.is_sanctioned
                            ? `⚠️ SANCTIONED - Match Score: ${Math.round((entity.match_score || 0) * 100)}%`
                            : `✓ Clear - ${entity.lists_checked?.length || 0} lists checked`
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Required Actions */}
            {report.required_actions && report.required_actions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Required Actions:
                </Typography>
                <List dense>
                  {report.required_actions.map((action: any, index: number) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <InfoIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={action.description}
                        secondary={`Priority: ${action.priority}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Recommendations */}
            {report.recommendations && report.recommendations.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Recommendations:
                </Typography>
                <List dense>
                  {report.recommendations.map((rec: string, index: number) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <InfoIcon fontSize="small" color="info" />
                      </ListItemIcon>
                      <ListItemText primary={rec} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* View Full Report Button */}
            {onViewDetails && (
              <Button
                variant="outlined"
                fullWidth
                onClick={onViewDetails}
                sx={{ mt: 2 }}
              >
                View Full Compliance Report
              </Button>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ComplianceReportCard;
